import { gettext } from '@eventbrite/i18n';
import { setLocationData } from '@eventbrite/redux-destination';
import { trackAnalyticsEvent } from '@eventbrite/site-analytics';
import { keysCamelToSnake } from '@eventbrite/transformation-utils';
import loadable from '@loadable/component';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { FormattedLocationResponse } from '../../api/location';
import './CategoryBrowseLocationAutocomplete.scss';
import { LocationSelectError } from './types';

const LazyLocationAutocomplete = loadable.lib(
    () => import('@eventbrite/location-autocomplete'),
);

interface AutocompleteRendererProps {
    match: any;
    location: any;
    history: any;
    setIsError: (error: LocationSelectError) => void;
    displayLocation?: string;
    currentLocation?: FormattedLocationResponse;
    onLocationSelect: () => void;
    resolveLocationSelect: () => void;
    handleRenderingFullAutocomplete: (val: boolean) => void;
}

export const AutocompleteRenderer: React.FunctionComponent<
    AutocompleteRendererProps
> = (props) => {
    const queryClient = useQueryClient();

    const handleLocationSelect = (location: FormattedLocationResponse) => {
        if (location.slug) {
            const oldSlug = props.match.params.locationSlug;
            const newSlug = location.slug;

            const newPath = props.match.url.replace(oldSlug, newSlug);
            const newPathWithSearch = `${newPath}${props.location.search}`;

            //set the location query here immediately
            queryClient.setQueryData(
                ['category-browse', location.slug],
                location,
            );

            //store most recently selected location
            //in the location cookie
            setLocationData(keysCamelToSnake(location));

            //update URL
            props.history.push(newPathWithSearch);

            trackAnalyticsEvent({
                category: 'category-page',
                action: 'LocChange',
                label: `${props.displayLocation}-${location.currentPlace}`,
            });
        } else {
            props.setIsError({ location, isError: true });
        }

        props.resolveLocationSelect();
    };

    return (
        <div className="category-browse__location-autocomplete--input-container">
            <LazyLocationAutocomplete>
                {({ LocationAutocomplete, getLocationSuggestions }: any) => (
                    <LocationAutocomplete
                        storeQuery={true}
                        placeholder={gettext('Select location').toString()}
                        currentPlace={props.displayLocation}
                        placeId={props.currentLocation?.placeId}
                        isOnline={props.currentLocation?.isOnline}
                        onLocationSelect={props.onLocationSelect}
                        handleLocationSelect={handleLocationSelect}
                        locationSuggestions={getLocationSuggestions(
                            {
                                currentPlace:
                                    props.currentLocation?.currentPlace,
                                suggestions: [],
                            },
                            true,
                        )}
                        autocompleteProps={{
                            defaultIsActive: true,
                            id: 'category-browse-location-autocomplete',
                            preventInputValuePrefill: true,
                            isRenderingSuggestions:
                                props.handleRenderingFullAutocomplete,
                        }}
                    />
                )}
            </LazyLocationAutocomplete>
        </div>
    );
};

export default AutocompleteRenderer;
